import * as R from 'ramda';
import { useMemo } from 'react';
import { useSelector } from 'react-redux';

import {
  LearningPathsUtils,
  playerSessionsSelectors,
  selectPlan,
  selectUserCompliance,
  useFetchLearningPathsQuery,
  useFetchOrgPinnedLpsQuery,
} from '@/store';
import { LearningPathWithDuration } from '@/types';

import { sortAndGroupLearningPaths, sortLps } from '../utils';

export type GroupedLearningPaths = Record<
  string,
  { groupKey: string; lps: LearningPathWithDuration[] }
>;

export const useLearningPathsData = () => {
  const compliance = useSelector(selectUserCompliance);
  const playerSessions = useSelector(playerSessionsSelectors.selectEntities);
  const { data: plan } = useSelector(selectPlan);

  const { data: orgPinnedLps, isLoading: isOrgPinnedLpsLoading } =
    useFetchOrgPinnedLpsQuery();
  const { data: learningPaths, isLoading: isLearningPathsLoading } =
    useFetchLearningPathsQuery({ compliance });

  const lpsWithDurationLeft = useMemo(() => {
    if (!learningPaths) return [];
    return sortLps(
      LearningPathsUtils.calculateProgress({
        lps: learningPaths,
        playerSessions,
      }),
    );
  }, [learningPaths, playerSessions]);

  const latestLps = useMemo(
    () =>
      [...lpsWithDurationLeft].sort(
        (a, b) =>
          new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
      ),
    [lpsWithDurationLeft],
  );

  const sortedInProgressLps = useMemo(
    () =>
      [...lpsWithDurationLeft]
        .filter(lp => lp.durationPlayed > 0)
        .sort((a, b) => {
          if (a.isCompleted !== b.isCompleted) {
            return a.isCompleted ? 1 : -1;
          }
          return (
            new Date(b.lastPositionEventAt).getTime() -
            new Date(a.lastPositionEventAt).getTime()
          );
        }),
    [lpsWithDurationLeft],
  );

  const groupedByCategoryLps: GroupedLearningPaths = useMemo(
    () =>
      R.indexBy(
        R.prop('groupKey'),
        sortAndGroupLearningPaths(lpsWithDurationLeft),
      ),
    [lpsWithDurationLeft],
  );

  const recommendedLps = useMemo(() => {
    if (!orgPinnedLps) return [];
    return orgPinnedLps
      .map(lp => lpsWithDurationLeft.find(l => l.groupId === lp.groupId))
      .filter((lp): lp is LearningPathWithDuration => lp !== undefined);
  }, [lpsWithDurationLeft, orgPinnedLps]);

  return {
    lpsWithDurationLeft,
    latestLps,
    sortedInProgressLps,
    recommendedLps,
    groupedByCategoryLps,
    organizationName: plan?.organizationName,
    isLoading: isOrgPinnedLpsLoading || isLearningPathsLoading,
  };
};
